import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearSingleAppDraft,
  getSingleAppDraft,
  reconcileSingleAppDraft,
  saveSingleAppDraft,
  syncSingleAppDraft,
  type SingleAppDraftInput,
} from "@/lib/api/single-app-drafts";
import { jobKeys } from "@/features/jobs/api";

export const singleAppDraftKeys = {
  all: ["single-app-drafts"] as const,
  detail: (connectionId: string, appId: string) =>
    [...singleAppDraftKeys.all, connectionId, appId] as const,
};

export const singleAppDraftQueryOptions = (connectionId: string, appId: string) =>
  queryOptions({
    queryKey: singleAppDraftKeys.detail(connectionId, appId),
    queryFn: () => getSingleAppDraft(connectionId, appId),
    enabled: !!connectionId && !!appId,
  });

export function useSingleAppDraft(connectionId: string, appId: string) {
  return useQuery(singleAppDraftQueryOptions(connectionId, appId));
}

export function useSaveSingleAppDraft(connectionId: string, appId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SingleAppDraftInput) => saveSingleAppDraft(connectionId, appId, input),
    onSuccess: (draft) => {
      queryClient.setQueryData(singleAppDraftKeys.detail(connectionId, appId), draft);
    },
  });
}

export function useClearSingleAppDraft(connectionId: string, appId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clearSingleAppDraft(connectionId, appId),
    onSuccess: () => {
      queryClient.setQueryData(singleAppDraftKeys.detail(connectionId, appId), null);
    },
  });
}

export function useSyncSingleAppDraft(connectionId: string, appId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncSingleAppDraft(connectionId, appId),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: singleAppDraftKeys.detail(connectionId, appId) }),
        queryClient.invalidateQueries({ queryKey: jobKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(result.jobId) }),
      ]);
    },
  });
}

export function useReconcileSingleAppDraft(connectionId: string, appId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => reconcileSingleAppDraft(jobId),
    onSuccess: async (draft, jobId) => {
      queryClient.setQueryData(singleAppDraftKeys.detail(connectionId, appId), draft);
      await queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
    },
  });
}
